import { audioRecordingModule } from "@/modules/audio-recording";
import { WebMediaTranscriber } from "@/store/audio/recording/media-transcriber/web-media-transcriber";

/**
 * Transcribes a recorded clip by replaying it through the same realtime
 * pipeline push-to-talk uses live: decode → MediaStreamDestination →
 * AssemblyAI websocket. Runs at 1× speed, so it takes about as long as the
 * clip — acceptable for the rare offline/dropped-stream recovery path, and it
 * needs no additional backend surface.
 *
 * Same teardown discipline as the live path: switch the worklet from source
 * audio to realtime silence, then wait for the provider's final-turn event.
 */
export const transcribeBlobViaRealtime = async (
  blob: Blob,
  { customVocabulary }: { customVocabulary?: string[] } = {}
): Promise<string> => {
  const audioContext = new AudioContext();

  try {
    const audioBuffer = await audioContext.decodeAudioData(await blob.arrayBuffer());
    const destination = audioContext.createMediaStreamDestination();
    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(destination);

    const finalTranscripts: string[] = [];
    let latestPartial = "";

    const transcriber = new WebMediaTranscriber({
      tokenProvider: audioRecordingModule.assemblyAiTokenProvider,
      stream: destination.stream,
      customVocabulary,
      requireCompleteFinalization: true,
    });
    transcriber.setEventHandlers({
      onTranscriptFinal: ({ finalTurn }) => {
        finalTranscripts.push(finalTurn.transcript);
        latestPartial = "";
      },
      onTranscriptPartial: ({ partialTurn }) => {
        latestPartial = partialTurn.transcript;
      },
    });

    try {
      await transcriber.start();

      const playbackDone = new Promise<void>(resolve => {
        source.onended = () => resolve();
      });
      source.start();
      await playbackDone;

      await transcriber.finalizePendingAudio();
      await transcriber.stop();

      // Termination is the server's proof that all final turns were emitted.
      // A surviving partial means that proof did not include the open turn;
      // retry the complete persisted clip instead of posting clipped text.
      if (latestPartial.trim().length > 0) {
        throw new Error("Recorded voice clip ended with an unfinalized transcript turn.");
      }
    } finally {
      transcriber.dispose();
    }

    return finalTranscripts
      .map(text => text.trim())
      .filter(Boolean)
      .join(" ");
  } finally {
    void audioContext.close().catch(() => undefined);
  }
};
